import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

const mdComponents: Components = {
  a: ({ href, children, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-brand underline decoration-brand/40 underline-offset-2 transition hover:text-brand-hover hover:decoration-brand-hover"
      {...props}
    >
      {children}
    </a>
  ),
  code: ({ className, children, ...props }) => {
    const inline = !className?.includes('language-');
    if (inline) {
      return (
        <code
          className="rounded-md border border-subtle/60 bg-base/90 px-1.5 py-0.5 font-mono text-[0.88em] text-th-primary"
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }) => (
    <pre
      className="my-3 overflow-x-auto rounded-xl border border-subtle/60 bg-base/90 p-3 font-mono text-[13px] leading-relaxed text-th-primary shadow-inner"
      {...props}
    >
      {children}
    </pre>
  ),
  ul: ({ children, ...props }) => (
    <ul className="my-2 list-disc space-y-1 pl-5 marker:text-accent/90" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="my-2 list-decimal space-y-1 pl-5 marker:text-th-secondary" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className="leading-relaxed [&>p]:my-1" {...props}>
      {children}
    </li>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="my-3 border-l-[3px] border-accent/50 bg-overlay/30 py-1 pl-4 pr-2 text-th-secondary [&_p]:my-1"
      {...props}
    >
      {children}
    </blockquote>
  ),
  h1: ({ children, ...props }) => (
    <h1 className="mb-2 mt-4 text-lg font-semibold tracking-tight text-th-primary first:mt-0" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="mb-2 mt-3 text-base font-semibold tracking-tight text-th-primary first:mt-0" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="mb-1.5 mt-3 text-[15px] font-semibold text-th-primary first:mt-0" {...props}>
      {children}
    </h3>
  ),
  p: ({ children, ...props }) => (
    <p className="mb-3 last:mb-0 leading-relaxed [&:first-child]:mt-0" {...props}>
      {children}
    </p>
  ),
  hr: ({ ...props }) => <hr className="my-4 border-subtle/60" {...props} />,
  table: ({ children, ...props }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-subtle/50">
      <table className="w-full min-w-[240px] border-collapse text-left text-[14px]" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }) => <thead className="bg-overlay/50 text-th-primary" {...props}>{children}</thead>,
  th: ({ children, ...props }) => (
    <th className="border-b border-subtle/60 px-3 py-2 font-semibold" {...props}>
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td className="border-b border-subtle/40 px-3 py-2 text-th-primary/95" {...props}>
      {children}
    </td>
  ),
  tr: ({ children, ...props }) => <tr className="border-subtle/40" {...props}>{children}</tr>,
};

const wrapperClass =
  'min-w-0 text-[15px] leading-relaxed text-th-primary [&_strong]:font-semibold [&_strong]:text-th-primary [&_em]:italic';

type AgentMessageMarkdownProps = {
  content: string;
  /** Blinking caret after text while the assistant message is still streaming (ChatGPT-style). */
  streamCursor?: boolean;
};

export function AgentMessageMarkdown({ content, streamCursor }: AgentMessageMarkdownProps) {
  return (
    <div className={wrapperClass}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
        {content}
      </ReactMarkdown>
      {streamCursor ? (
        <span
          className="ml-px inline-block h-[1.05em] w-0.5 min-h-[1em] animate-pulse rounded-sm bg-th-primary/85 align-baseline"
          aria-hidden
        />
      ) : null}
    </div>
  );
}
